import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Property } from 'src/app/models/property.model';
import { AuthService } from 'src/app/services/auth.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { LayoutService } from 'src/app/services/layout.service';
import { MFilesService } from 'src/app/services/mfiles.service';
import { PageService } from 'src/app/services/page.service';
import { MFObjectSizePipe } from 'src/app/shared/pipes/mfObjectSize.pipe';
import { PreviewButtonRendererComponent } from './preview-button/preview_button-renderer.component';
import { TypeaheadSelectRendererComponent } from './typeahead-select/typeahead-select.component';

@Component({
	selector: 'app-view-widget',
	templateUrl: './view-widget.component.html',
	styleUrls: ['./view-widget.component.css'],
	animations: [
		trigger(
			'myAnimation',
			[
				transition(
					':enter', [
					style({ opacity: 0 }),
					animate('0.1s 100ms ease-out', style({ 'opacity': 1 })),
				]
				),
				transition(
					':leave', [
					style({ 'opacity': 1 }),
					animate('0.1s 100ms ease-out', style({ 'opacity': 0 }))
				]
				)]
		)
	]
})
export class ViewWidgetComponent implements OnInit, OnDestroy {
	modalRef: BsModalRef;

	@Input() view: any;
	@Input() inGrid = false;
	@Input() associateSelected = false;
	@Input() inheritSelected = false;
	@Input() autoSubmit = false;
	@Input() associatedProp: number;
	@Input() contextualCheckin = false;
	@Input() revisionsEnabled = true;
	@Input() defaultClass: number;
	@Input() hasAccessProperties = false;
	@Input() additionalProps: Property[];
	@Input() autopopulateDocName = false;
	@Input() nameProp: number;
	@Input() allowUsersToAddProps = true;
	@Input() widgetEditMode = false;
	@Input() filelessUploads = [];
	@Input() objectTypeClasses = [];
	@Input() conditionalUploadsClasses = [];
	@Input() conditionalUploadsStates = [];
	@Input() workflowStates = [];
	@Input() automaticWorkflowTransitions = false;
	@Input() startingState: number;
	@Input() targetState: number;
	@Input() disableRelatedView = false;
	@Input() massMetadataUpdates = false;

	@Output() openedView = new EventEmitter<string>();
	@Output() objectType = new EventEmitter<number>();
	@Output() uploadConfirm = new EventEmitter<any>();
	@Output() fileToggledEvent = new EventEmitter<any>();

	documentSelectionSubscription: Subscription;
	loadViewSubscription: Subscription;

	editMode = false;

	tiles = false;
	coauthorEnabled: boolean;
	completedEnabled = false;
	completedCriteriaField: number;
	completedField: number;
	vaultProperties = [];
	objectTypes = {};
	objectLists = {};
	propValueListMap = {};
	uploadsDisabled = true;

	activeType: string;
	activeIndex: number;
	activeView: any;
	completeCriteria = [];

	relatedItems = [];

	animStart = false;
	animDone = true;

	formValid = false;
	formProperties: Property[][] = [];
	filelessObjectType: number;
	filelessClass: number;
	filelessPermissions = [];

	useMfilesIcons = true;
	objectTypeIcons = {};
	page: number;

	selectedItem: any;
	sortRelatedAsc = true;

	associatedObjectType: number;
	associatedObject: number;

	// AG GRID
	agGridColumnDefs = [];
	agGridRowData = [];
	agGridColumnApi: any;
	agGridApi: any;
	copyEvent = {
		value: '',
		column: null
	};
	recentClickedCellColDef: any;
	agGridChangesMade = false;
	changedObjs = {};
	frameworkComponents = {};
	context = {};

	deleteName = '';
	deleteType: number;
	deleteItem: number;

	defaultDateFormat: string;

	constructor(
		private mfilesService: MFilesService,
		public configService: ConfigurationService,
		private pageService: PageService,
		private router: Router,
		private modalService: BsModalService,
		private domSanitizer: DomSanitizer,
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private mfObjectSizePipe: MFObjectSizePipe,
		private datePipe: DatePipe,
		private layoutService: LayoutService,
		private authService: AuthService
	) { }

	ngOnInit() {
		this.view.loading = true;
		this.route.queryParams.subscribe(params => {
			if (params.path) {
				this.view.path = params.path;
			}
		});

		this.configService.list(false).subscribe((result) => {
			for (const config of result) {
				if (config.name === 'completedEnabled') {
					this.completedEnabled = config.value.toLowerCase() === 'true';
				} else if (config.name === 'completedCriteriaField') {
					this.completedCriteriaField = parseInt(config.value, 10);
				} else if (config.name === 'completedField') {
					this.completedField = parseInt(config.value, 10);
				} else if (config.name === 'coauthorEnabled') {
					this.coauthorEnabled = config.value.toLowerCase() === 'true';
				} else if (config.name === 'useMfilesIcons') {
					this.useMfilesIcons = config.value.toLowerCase() === 'true';
				} else if (config.name === 'defaultDateFormat') {
					this.defaultDateFormat = config.value;
				}
			}
		});

		this.mfilesService.getObjectTypes().subscribe((result) => {
			result.body.forEach((objectType) => {
				this.objectTypes[objectType.id] = objectType;
			});
		});

		this.mfilesService.getVaultProperties().subscribe((response) => {
			const properties: any = response.body;
			this.vaultProperties = properties;
			this.vaultProperties.push({ ID: -1, Name: 'Revision', DataType: 2 });

			this.mfilesService.getVaultValueLists().subscribe((responseValueLists) => {
				const valueLists: any = responseValueLists.body;
				for (const valueList of valueLists) {
					const propsUsingValueList: any[] = this.vaultProperties.filter(
						(prop) =>
							prop.valueList !== 0 &&
							(prop.dataType === 9 || prop.dataType === 10) &&
							prop.valueList === valueList.id
					);
					for (const prop of propsUsingValueList) {
						this.propValueListMap[prop.id] = valueList;
					}
				}

				this.loadView(this.view);
			});

		});

		this.documentSelectionSubscription = this.pageService.documentSelectionOccurred.subscribe((context: any) => {
			this.uploadsDisabled = true;

			let unlockClass = false;
			let unlockState = false;

			if (this.conditionalUploadsClasses.length == 0 && this.conditionalUploadsStates.length == 0) {
				this.uploadsDisabled = false;
			} else {
				if (this.conditionalUploadsClasses.length > 0) {
					for (const uploadClass of this.conditionalUploadsClasses) {
						if (uploadClass.class == context.itemClass) {
							unlockClass = true;
						}
					}
				} else {
					unlockClass = true;
				}

				if (this.conditionalUploadsStates.length > 0) {
					for (const uploadState of this.conditionalUploadsStates) {
						if (uploadState.state == context.workflowState) {
							unlockState = true;
						}
					}
				} else {
					unlockState = true;
				}
				if (unlockClass && unlockState) {
					this.uploadsDisabled = false;
				}
			}




		});

		this.route.params.subscribe((params: Params) => {
			this.page = parseInt(params['page'], 10);
		});

		const classes = [];
		for (const classInfo of this.filelessUploads) {
			classes.push(classInfo.class);
		}
		if (classes.length > 0) {
			this.mfilesService.checkClassPermissions(classes).subscribe((result) => {
				const response: any = result.body;
				this.filelessPermissions = response;
			});
		}
	}

	ngOnDestroy(): void {
		if (this.loadViewSubscription) {
			this.loadViewSubscription.unsubscribe();
		}
	}

	setTiles(tiles: boolean) {
		this.tiles = tiles;
	}

	// Limitation:
	//  - If read/write/delete access properties are enabled, there are some limitations in how
	//    this will work with the view structure in M-Files. The filtering will always take place
	//    at the first grouping level after the last view. This means that the following structures
	//    will work:
	//      View 1
	//        View 2
	//          Grouping Level 1
	//            Grouping Level 2
	//              Grouping Level 3
	//                etc.
	//    Grouping Level 1 will be filtered with the access properties since it is the first grouping
	//    level after the last view. The following structure will NOT work (if the goal is to filter at
	//    Grouping Level 2):
	//      View 1
	//        Grouping Level 1
	//          View 2 (inside grouping level 1)
	//            Grouping level 2
	//    Grouping Level 1 will be filtered by the access properties.
	loadView(view: any) {
		this.associatedObject = undefined;
		this.associatedObjectType = undefined;

		view.loading = true;
		this.mfilesService.getShareView(view.path).subscribe((result) => {
			view.viewContents = result;
			view.sortColumn = 'dateModified';
			view.sortAsc = true;
			this.sort('dateModified', view);
			view.isRevision = false;

			const iconsToLoad = [];
			for (const item of view.viewContents.items) {
				if (iconsToLoad.indexOf(item.type) === -1) {  // If the item type is not already in the iconsToLoad array, add and retrieve it
					iconsToLoad.push(item.type);
					this.getIcon(item.type);
				}
			}

			this.agGridColumnDefs = [];
			this.agGridRowData = [];

			if (view.viewContents.items && view.viewContents.items.length > 0 &&
				view.viewContents.columns && view.viewContents.columns.length > 0) {
				this.getColumnsForAgGrid();
				this.getRowsForAgGrid();
			}

			view.loading = false;
		});
	}

	getIcon(type: number) {
		if (type !== 0 && this.useMfilesIcons) {
			this.mfilesService.getObjectTypeIcon(type).subscribe((imageResult) => {
				if (imageResult.body && imageResult.body.size !== 0) {
					const reader = new FileReader();
					reader.onloadend = (e) => {
						this.objectTypeIcons[type] = this.domSanitizer.bypassSecurityTrustUrl(reader.result.toString());
					};
					reader.readAsDataURL(imageResult.body);
				} else {
					this.objectTypeIcons[type] = undefined;
				}
			});
		}
	}

	sort(field: string, view: any) {
		if (view.sortColumn === field) {
			view.sortAsc = !view.sortAsc;
		} else {
			view.sortAsc = true;
		}
		view.sortColumn = field;

		if (view.sortAsc) {
			if (field === 'name') {
				view.viewContents.items.sort((a, b) => (a.name > b.name) ? 1 : -1);
			} else if (field === 'dateCreated') {
				view.viewContents.items.sort((a, b) => (a.dateCreated > b.dateCreated) ? 1 : -1);
			} else if (field === 'dateModified') {
				view.viewContents.items.sort((a, b) => (a.dateModified > b.dateModified) ? 1 : -1);
			} else if (field === 'version') {
				view.viewContents.items.sort((a, b) => (a.version > b.version) ? 1 : -1);
			} else if (field === 'type') {
				view.viewContents.items.sort((a, b) => (a.type > b.type) ? 1 : -1);
			}
		} else {
			if (field === 'name') {
				view.viewContents.items.sort((a, b) => (a.name < b.name) ? 1 : -1);
			} else if (field === 'dateCreated') {
				view.viewContents.items.sort((a, b) => (a.dateCreated < b.dateCreated) ? 1 : -1);
			} else if (field === 'dateModified') {
				view.viewContents.items.sort((a, b) => (a.dateModified < b.dateModified) ? 1 : -1);
			} else if (field === 'version') {
				view.viewContents.items.sort((a, b) => (a.version < b.version) ? 1 : -1);
			} else if (field === 'type') {
				view.viewContents.items.sort((a, b) => (a.type < b.type) ? 1 : -1);
			}
		}
	}

	sortRelated(field: string) {
		if (this.sortRelatedAsc) {
			this.sortRelatedAsc = !this.sortRelatedAsc;
			if (field === 'name') {
				this.relatedItems.sort((a, b) => (a.name > b.name) ? 1 : -1);
			} else if (field === 'dateCreated') {
				this.relatedItems.sort((a, b) => (Date.parse(a.dateCreated) > Date.parse(b.dateCreated)) ? 1 : -1);
			} else if (field === 'dateModified') {
				this.relatedItems.sort((a, b) => (Date.parse(a.dateModified) > Date.parse(b.dateModified)) ? 1 : -1);
			} else if (field === 'version') {
				this.relatedItems.sort((a, b) => (a.version > b.version) ? 1 : -1);
			} else if (field === 'type') {
				this.relatedItems.sort((a, b) => (a.type > b.type) ? 1 : -1);
			}
		} else {
			this.sortRelatedAsc = !this.sortRelatedAsc;
			if (field === 'name') {
				this.relatedItems.sort((a, b) => (a.name < b.name) ? 1 : -1);
			} else if (field === 'dateCreated') {
				this.relatedItems.sort((a, b) => (Date.parse(a.dateCreated) < Date.parse(b.dateCreated)) ? 1 : -1);
			} else if (field === 'dateModified') {
				this.relatedItems.sort((a, b) => (Date.parse(a.dateModified) < Date.parse(b.dateModified)) ? 1 : -1);
			} else if (field === 'version') {
				this.relatedItems.sort((a, b) => (a.version < b.version) ? 1 : -1);
			} else if (field === 'type') {
				this.relatedItems.sort((a, b) => (a.type < b.type) ? 1 : -1);
			}
		}
	}

	openView(path: string, view: any) {
		this.relatedItems = [];
		view.path = path;
		this.loadView(view);
		this.openedView.emit(path);
	}

	associateObjectType(classId: number) {
		this.associatedObjectType = classId;
	}

	associateObject(itemId: number) {
		this.associatedObject = itemId;
	}

	createProperty(dataType: number, value: string, required: boolean, id: number): Property {
		const property: Property = {
			dataType: dataType,
			name: '',
			value: value,
			required: required,
			id: id,
			valueList: null,
			hasStaticFilter: false,
			staticFilters: null
		};
		return property;
	}

	validateMultiselectValues(prop: Property) {
		if (prop.dataType === 10 && typeof prop.value !== 'string') {
			const valueArr: Property[] = prop.value;
			let joined = '';
			valueArr.forEach(val => {
				joined += val.id + ',';
			});
			prop.value = joined.replace(/,\s*$/, '');
		}
		return prop;
	}

	onUploadQueueChange(view: any, event: any) {
		view.files = event;
	}

	openFile(fileId: number) {
		window.open(window.location.origin + '/#/file/' + fileId + '/0', '_blank');
	}

	onFormChange(forms: UntypedFormGroup[]) {
		let valid = true;
		if (forms.length === 0) {
			this.formValid = false;
		}
		for (const form of forms) {
			if (!form.valid) {
				valid = false;
				break;
			}
		}
		this.formValid = valid;
	}

	onPropertiesChange(formProperties: Property[][]) {
		this.formProperties = formProperties;
	}

	relatedLoaded(e: any) {
		this.selectedItem = e['item'];
		const related = e['related'];
		const iconsToLoad = [];
		for (const item of related) {
			if (!item.type && item.objVer) {
				item.name = item.title;
				item.type = item.objVer.type;
				item.version = item.objVer.version;
				item.dateCreated = item.created;
				item.dateModified = item.lastModified;
			}

			if (!this.objectTypeIcons[item.type] && iconsToLoad.indexOf(item.type) === -1) {
				iconsToLoad.push(item.type);
				this.getIcon(item.type);
			}
		}

		this.relatedItems = related;
	}

	animationStart() {
		this.animStart = true;
		this.animDone = false;
	}

	animationDone() {
		this.animStart = false;
		this.animDone = true;
	}

	showUploadDocumentButton() {
		if (this.associateSelected && !this.associatedObject) {
			return false;
		}
		if (this.authService.isExternalUser() && this.hasAccessProperties) {
			const pathSegments: string[] = this.view.path.split('/').filter(folder => folder.length > 0);

			// See Limitation comment above loadView()
			const firstGroupingLevelIndex: number = pathSegments.findIndex(segment => segment.match('L'));
			const firstGLAfterLastView: string = pathSegments[firstGroupingLevelIndex];
			const contextFolder: string = firstGLAfterLastView;

			if (contextFolder) {
				return this.view.path.indexOf('L') > -1 &&  // Check to make sure the user is in a virtual folder
					(
						// Check to make sure they have r/w or r/w/d access to the virtual folder
						this.view.rwFolders.includes(contextFolder) ||
						this.view.rwdFolders.includes(contextFolder)
					);
			}
		} else {
			return this.view.path;
		}
	}

	toggleEditMode() {
		this.editMode = !this.editMode;
	}

	createCopyPasteEventListeners() {
		const gridCells = document.querySelectorAll('div.ag-cell');
		gridCells.forEach(cell => {
			cell.addEventListener('copy', (event => {
				this.copyEvent.value = document.getSelection().anchorNode.nodeValue;
				navigator.clipboard.writeText(this.copyEvent.value);
				this.copyEvent.column = this.recentClickedCellColDef;
				this.toastr.info('Cell value copied!');
			}));

			cell.addEventListener('paste', (event => {
				if (this.copyEvent.value && this.copyEvent.column) {
					const selectedRows = this.agGridApi.getSelectedRows();
					selectedRows.forEach(row => {
						const rowIndex = this.agGridRowData.indexOf(row);
						this.agGridRowData[rowIndex][this.copyEvent.column.field] = this.copyEvent.value;

						const newValue = this.copyEvent.value;
						const colDataType = this.copyEvent.column.dataType;
						const colId = this.copyEvent.column.id;

						if (colDataType === 9 || colDataType === 10 && this.agGridRowData[rowIndex].vliMappings[this.copyEvent.column.valueList]) {
							this.agGridRowData[rowIndex].vliMappings[this.copyEvent.column.valueList] = this.getLookupId(colId, newValue);
						}

						const changedProp = {
							propertyDef: colId === -1 ? 0 : colId,
							dataType: colDataType,
							value: colDataType === 9 || colDataType === 10 ? this.getLookupId(colId, newValue) : newValue
						};
						this.addOrUpdateChangedItem(row.itemId, row.itemType, row.itemVersion, changedProp);
					});

					this.agGridChangesMade = true;
					this.agGridApi.setRowData(this.agGridRowData);
					this.createCopyPasteEventListeners();
				} else {
					this.toastr.info('Please copy a cell before pasting.');
				}

			}));
		});
	}

	determineAgGridHeight() {
		const widgetContainerHeight = document.querySelector<HTMLElement>('.dataview-widget-outer').getBoundingClientRect().height;
		document.querySelector<HTMLElement>('#ag-grid').style.height = `${widgetContainerHeight - 193}px`;
	}

	onGridReady(params) {
		this.agGridApi = params.api;
		this.agGridColumnApi = params.columnApi;

		this.determineAgGridHeight();

		this.layoutService.screenHeightChanged.subscribe(() => {
			setTimeout(() => {
				this.determineAgGridHeight();
			}, 300);
		});

		this.createCopyPasteEventListeners();
	}

	isMetadataOrViewerPresent() {
		const widgets = this.pageService.getActivePageWidgets();
		let widgetFound = false;
		for (const widget of widgets) {
			if (widget.type === 'viewer' || widget.type === 'metadata') {
				widgetFound = true;
			}
		}

		return widgetFound;
	}

	openPreview(fileId: number, type: number) {
		// Determine if there is a metadata or viewer  widget on the page
		const widgetFound = this.isMetadataOrViewerPresent();

		// If there is, emit that a document selection has occurred so the widgets can retrieve their data
		if (widgetFound) {
			const context = {
				itemId: fileId,
				type: type
			};
			this.pageService.documentSelectionOccurred.emit(context);
		} else {
			this.router.navigate(['file', fileId, 0]);

			// In case we need to open in new tab in future
			// window.open(window.location.origin + '/#/file/' + fileId + query, '_blank');
		}
	}

	addOrUpdateChangedItem(itemId: number, itemType: number, itemVersion: number, property: any) {
		if (this.changedObjs[itemId]) {
			const existingPropIndex = this.changedObjs[itemId].properties.findIndex(prop => prop.propertyDef === property.propertyDef);
			if (existingPropIndex === -1) {
				this.changedObjs[itemId].properties.push(property);
			} else {
				this.changedObjs[itemId].properties[existingPropIndex] = property;
			}
		} else {
			this.changedObjs[itemId] = {
				objVer: {
					id: itemId,
					type: itemType,
					version: itemVersion
				},
				properties: [property]
			};
		}
	}

	getValuesForSelectList(params) {
		if (params.colDef.dataType === 8) {
			return ['Yes', 'No'];
		} else {
			const propValueListMapping = this.propValueListMap[params.colDef.id];
			if (this.propValueListMap[params.colDef.id].hasOwner) {
				const ownerListId = propValueListMapping.owner;
				if (params.data.vliMappings && params.data.vliMappings[ownerListId]) {
					const ownerListValueId = params.data.vliMappings[ownerListId];
					const result = this.propValueListMap[params.colDef.id].items
						.filter((item: any) => item.ownerId === ownerListValueId)
						.map((item: any) => item.name);

					result.unshift('-');
					return result;
				} else {
					return ['-'];
				}
			} else {
				return this.propValueListMap[params.colDef.id].items.map(val => val.name);
			}
		}
	}

	getColDataType(colId: number) {
		let prop = null;

		for (const vaultProp of this.vaultProperties) {
			if (colId === -1) {   // Search for Name property
				if (vaultProp.id === 0) {
					prop = vaultProp;
					break;
				}
			} else {
				if (vaultProp.id === colId) {
					prop = vaultProp;
					break;
				}
			}
		}

		if (prop) {
			return prop.dataType;
		}
	}

	getColumnsForAgGrid(refresh?: boolean) {
		if (refresh) {
			this.agGridColumnDefs = [];
		}

		if (this.agGridColumnDefs.length === 0) {
			const viewColumns = this.view.viewContents.columns;

			this.agGridColumnDefs.push({
				field: 'Preview',
				editable: false,
				position: 0,
				width: '100px',
				resizable: false,
				cellRenderer: 'previewButtonRenderer'
			});

			this.context = { componentParent: this };
			this.frameworkComponents = {
				previewButtonRenderer: PreviewButtonRendererComponent,
				typeaheadSelectRenderer: TypeaheadSelectRendererComponent
			};

			viewColumns.forEach(col => {
				const colDataType: number = this.getColDataType(col.id);
				let useDefaultColumn = false;

				let column: any;
				if (col.id > 999 || col.id === 100 &&
					colDataType === 9 || colDataType === 10) {
					if (this.propValueListMap[col.id]) {
						column = {
							field: col.name,
							editable: col.id > 999 || col.id === 100,
							id: col.id,
							position: col.position,
							dataType: colDataType,
							valueList: this.propValueListMap[col.id].id,
							resizable: true,
							filter: true,
							sortable: true,
							cellEditor: 'typeaheadSelectRenderer',
							cellEditorParams: (params) => {
								return {
									values: this.getValuesForSelectList(params)
								};
							}
						};
					} else {
						useDefaultColumn = true;
					}
				} else {
					useDefaultColumn = true;
				}

				if (useDefaultColumn) {
					column = {
						field: col.name,
						editable: col.id > 999 || col.id === -1,
						id: col.id,
						position: col.position,
						dataType: colDataType,
						resizable: true,
						filter: true,
						sortable: true,
						cellEditor: '',
						cellEditorParams: {}
					};
				}

				this.agGridColumnDefs.push(column);
			});
		}
	}

	myCellRenderer(params) {
		return '<a href="javascript:" (click)="openFile(' + params.id + ', ' + params.type + ')" ' +
			'*ngIf="' + params.type + ' === 0">' + params.name + '</a>';
	}

	getRowsForAgGrid(refresh?: boolean) {
		if (refresh) {
			this.agGridRowData = [];
		}

		if (this.agGridRowData.length === 0) {
			const items = this.view.viewContents.items;
			items.forEach(item => {
				const row = { itemId: item.id, itemType: item.type, itemVersion: item.version };
				this.view.viewContents.columns.forEach(col => {
					switch (col.id) {
						case -1: // Name
							row[col.name] = item.name;
							break;
						case -2: // Type
							row[col.name] = this.objectTypes[item.type].name;
							break;
						case -3: // Size
							row[col.name] = this.mfObjectSizePipe.transform(this.getItemSize(item));
							break;
						case -8: // Version
							row[col.name] = item.version;
							break;
						case -13: // Date Created
							row[col.name] = this.datePipe.transform(item.dateCreated, 'M/d/yyyy h:mm a');
							break;
						case -14: // Date Modified
							row[col.name] = this.datePipe.transform(item.dateModified, 'M/d/yyyy h:mm a');
							break;
						default:
							const info = this.getDisplayValueForColumn(col, item);

							row[col.name] = info.value;
							if (info.valueListID && info.vliID) {
								if (row['vliMappings']) {
									row['vliMappings'][info.valueListID] = info.vliID;
								} else {
									row['vliMappings'] = {};
									row['vliMappings'][info.valueListID] = info.vliID;
								}
							}
					}
				});

				this.agGridRowData.push(row);
			});
		}
	}

	getItemSize(item) {
		let size = 0;

		for (const prop of item.properties) {
			if (prop.propertyDef === 31) {
				size = prop.value.value;
				break;
			}
		}
		return size;
	}

	getDisplayValueForColumn(column: any, item: any) {
		const columnId = column.id;
		let columnProp = null;

		for (const prop of item.properties) {
			if (prop.propertyDef === columnId) {
				columnProp = prop;
				break;
			}
		}

		if (columnProp != null && columnProp.value.displayValue != null && columnProp.value.displayValue.length > 0) {
			if (columnProp.value.dataType === 9) {
				return {
					valueListID: this.propValueListMap[columnId].id,
					vliID: columnProp.value.lookup.item,
					value: columnProp.value.displayValue
				};
			}
			return {
				valueListID: null,
				vliID: null,
				value: columnProp.value.displayValue
			};
		} else {
			return {
				valueListID: null,
				vliID: null,
				value: '-'
			};
		}
	}

	getLookupId(propId: number, newValue: string) {
		const valueListItem = this.propValueListMap[propId].items.find(item => item.name === newValue);
		if (valueListItem) {
			return valueListItem.id;
		} else {
			return null;
		}
	}

	getBooleanValue(val: string) {
		return val === 'Yes';
	}

	onCellValueChanged(cell: any) {
		if (cell.newValue) {
			this.agGridChangesMade = true;

			const newValue = cell.data[cell.colDef.field];
			const colDataType = cell.colDef.dataType;
			const colId = cell.colDef.id;
			let colValue = newValue;

			if (colDataType === 8) {
				colValue = this.getBooleanValue(newValue);
			} else if (colDataType === 9 || colDataType === 10) {
				if (colValue === '-') {
					cell.data.vliMappings[cell.colDef.valueList] = null;
				} else {
					colValue = this.getLookupId(colId, newValue);
					const valueListForColProp = this.propValueListMap[cell.colDef.id];
					const newVli = valueListForColProp.items.find(item => item.name === cell.newValue);
					const newVliID = newVli.id;
					cell.data.vliMappings[cell.colDef.valueList] = newVliID;
				}
			}

			if (colValue) {
				const changedProp = {
					propertyDef: colId === -1 ? 0 : colId,
					dataType: colDataType,
					value: colValue === '-' ? null : colValue
				};

				this.addOrUpdateChangedItem(cell.data.itemId, cell.data.itemType, cell.data.itemVersion, changedProp);
			}
		}
	}

	onCellClicked(cell: any) {
		// Save the column definition just in case a copy event happens
		this.recentClickedCellColDef = cell.column.colDef;
	}

	saveChangedObjs() {
		this.view.loading = true;
		this.mfilesService.updatePropertiesOnMultipleObjs(Object.values(this.changedObjs)).subscribe((response) => {
			if (response && response.length > 0) {
				this.toastr.error(response);
			} else {
				this.toastr.success('Changes were saved successfully.');

				this.loadView(this.view);
				this.changedObjs = {};
				this.agGridChangesMade = false;
				this.editMode = false;

				this.getColumnsForAgGrid(true);
				this.getRowsForAgGrid(true);
			}

			this.view.loading = false;
		});
	}

	cancelChangedObjs() {
		this.changedObjs = {};
		this.agGridChangesMade = false;
		this.editMode = false;

		this.getColumnsForAgGrid(true);
		this.getRowsForAgGrid(true);
	}

	getClassName(upload: any) {
		let name = upload.class;
		for (const classObj of this.objectTypeClasses[upload.objectType]) {
			if (classObj.id === upload.class) {
				name = classObj.name;
				break;
			}
		}
		return name;
	}

	openModal(template: TemplateRef<any>, size: string) {
		this.modalRef = this.modalService.show(
			template,
			{ class: 'modal-' + size, backdrop: 'static', animated: false }
		);
	}

	hasDocuments() {
		let hasDocuments = false;
		if (this.view.viewContents.items) {
			for (const item of this.view.viewContents.items) {
				if (item.type === 0) {
					hasDocuments = true;
					break;
				}
			}
		}

		return hasDocuments;
	}

	showNoDocs() {
		const hasViews = this.view.viewContents.views && this.view.viewContents.views.length > 0;
		const hasTraditionalFolders = this.view.viewContents.traditionalFolders && this.view.viewContents.traditionalFolders.length > 0;
		const hasFolders = this.view.viewContents.folders && this.view.viewContents.folders.length > 0;

		return !hasViews && !hasTraditionalFolders && !hasFolders && !this.hasDocuments();
	}

	itemChecked(item: any, e: any) {
		this.fileToggledEvent.emit({ item, e });
	}
}
